/**
 * Stripe Connect — studio-side payment service.
 *
 * Studios connect their own Stripe account once via the onboarding
 * flow, then take payments on documents directly into that account.
 * Coexists with the existing Stripe subscription wiring (which is
 * for Orbit's own SaaS billing).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { toast } from '@/hooks/use-toast';

// ─── Types ───────────────────────────────────────────────────────

export interface StripeConnectAccount {
  id: string;
  org_id: string;
  stripe_account_id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  country: string | null;
  default_currency: string | null;
  onboarded_at: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface StripePayment {
  id: string;
  org_id: string;
  document_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_checkout_session_id: string | null;
  stripe_account_id: string;
  amount_cents: number;
  /** Total cents refunded across all refund events. Null = no refunds. */
  amount_refunded_cents: number | null;
  currency: string;
  /**
   * P1-1 — `scheduled` was missing from this union even though
   * 20260503_stripe_payment_plans.sql has written it since March: it's
   * the state every installment 2..N sits in until the daily sweep
   * charges it. The web payments table rendered those rows with a
   * fallback grey pill and no label anyone could read.
   */
  status:
    | 'pending'
    | 'scheduled'
    | 'succeeded'
    | 'failed'
    | 'refunded'
    | 'partial_refund';
  client_email: string | null;
  client_name: string | null;
  paid_at: string | null;
  refunded_at: string | null;
  metadata: unknown;
  created_at: string;
  updated_at: string;

  // ── Payment plans (20260503) ──────────────────────────────────
  payment_plan_id: string | null;
  /** 1-based. Null on standalone payments. */
  installment_index: number | null;
  installment_count: number | null;
  /** When the off-session charge is due. Null on standalone payments. */
  scheduled_at: string | null;
  charge_attempts: number | null;
  last_charge_attempt_at: string | null;

  // ── Failure detail (P1-1, migration 20260812120000) ───────────
  /** Stripe's decline/error code for the last failed attempt. */
  failure_code: string | null;
  /** Stripe's cardholder-facing message. Safe to show verbatim. */
  failure_message: string | null;
}

/**
 * Can this payment be refunded right now, and for how much?
 *
 * Mirrors `StudioPayment.isRefundable` / `.maxRefundableCents` in
 * ios/Orbit/Orbit/Data/PaymentsRepository.swift AND the server-side
 * guards in supabase/functions/stripe-refund-payment/index.ts:75-137.
 * All three have to agree — the edge function is the one that counts,
 * so these exist to stop the UI offering a refund the server will
 * reject, not to be the check itself.
 */
export function isRefundable(p: StripePayment): boolean {
  return p.status === 'succeeded' || p.status === 'partial_refund';
}

/** Original amount minus everything already sent back. */
export function maxRefundableCents(p: StripePayment): number {
  return Math.max(0, p.amount_cents - (p.amount_refunded_cents ?? 0));
}

// ─── Connect account: read + onboarding + status sync ────────────

/**
 * Read the org's Connect account row directly from Supabase. Doesn't
 * call Stripe — use `useRefreshConnectStatus()` to force a sync.
 */
export const useConnectAccount = () => {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ['stripe-connect-account', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from('stripe_connect_accounts')
        .select('*')
        .eq('org_id', orgId)
        .maybeSingle();
      if (error) throw error;
      return data as StripeConnectAccount | null;
    },
    enabled: !!orgId,
  });
};

/**
 * Kick off the onboarding flow. Calls the edge function which returns
 * a one-time Stripe-hosted URL — caller redirects the browser there.
 *
 * Wave 8.1a — short-circuits in Preview Studio with a neutral toast
 * so the audience never gets routed to the real Stripe onboarding.
 */
export const useStartConnectOnboarding = () => {
  const { isDemo } = useDemoMode();
  return useMutation({
    mutationFn: async () => {
      if (isDemo) {
        toast({
          title: 'Not available in this workspace',
          description: 'Payments will be enabled once you finish setup.',
        });
        return { url: '', stripe_account_id: '' };
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect-onboard`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error ?? `Failed (${resp.status})`);
      }
      return resp.json() as Promise<{ url: string; stripe_account_id: string }>;
    },
  });
};

/**
 * Force a fresh sync from Stripe. Used after the user returns from
 * the hosted onboarding flow to pick up the latest capability flags.
 *
 * Wave 8.1a — no-op in Preview Studio.
 */
export const useRefreshConnectStatus = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  return useMutation({
    mutationFn: async () => {
      if (isDemo) {
        toast({
          title: 'Not available in this workspace',
          description: 'Payments will be enabled once you finish setup.',
        });
        return {};
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect-status`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error ?? `Failed (${resp.status})`);
      }
      return resp.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-connect-account'] });
    },
  });
};

// ─── Payments list (studio side) ─────────────────────────────────

export const useStripePayments = () => {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ['stripe-payments', orgId],
    queryFn: async () => {
      if (!orgId) return [] as StripePayment[];
      const { data, error } = await supabase
        .from('stripe_payments')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as StripePayment[];
    },
    enabled: !!orgId,
  });
};

// ─── Refunds (studio side) ──────────────────────────────────────

/**
 * Issue a refund on a captured payment.
 *
 * - Pass `amountCents = undefined` for a full refund.
 * - Partial refunds reduce the remaining refundable balance; the
 *   payment row's status flips to 'partial_refund' until the entire
 *   amount has been returned.
 *
 * Webhook (charge.refunded) updates the row in the background — the
 * mutation invalidates the payments query on success so list views
 * pick up the change immediately rather than waiting on realtime.
 */
export const useRefundPayment = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  return useMutation({
    mutationFn: async (params: { paymentId: string; amountCents?: number; reason?: string }) => {
      if (isDemo) {
        toast({
          title: 'Not available in this workspace',
          description: 'Payments will be enabled once you finish setup.',
        });
        return {
          refund_id: '',
          amount_cents: params.amountCents ?? 0,
          currency: 'usd',
          status: 'skipped',
        };
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-refund-payment`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_id: params.paymentId,
          amount_cents: params.amountCents,
          reason: params.reason,
        }),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error ?? `Failed (${resp.status})`);
      }
      return resp.json() as Promise<{
        refund_id: string;
        amount_cents: number;
        currency: string;
        status: string;
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-payments'] });
    },
  });
};

// ─── Public: create a Checkout session for a document ────────────

/**
 * Called from the public `/d/:token` signing page when the client
 * clicks Pay. Returns the Stripe Checkout URL — caller redirects
 * the browser there.
 */
export async function createDocumentCheckout(
  documentToken: string,
  signerEmail?: string,
): Promise<{ url: string; session_id: string; amount_cents: number; currency: string }> {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-document-checkout`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Public flow — anon key as bearer (same pattern as the
      // intake form / portal queries; gating is via document_token).
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ document_token: documentToken, signer_email: signerEmail }),
  });
  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({}));
    throw new Error(errBody.error ?? `Failed (${resp.status})`);
  }
  return resp.json();
}
